import React, { useState, useRef, useEffect } from 'react';
import { Input } from 'reactstrap';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import useOutsideClick from 'utils/useOutsideClick';
import format from 'date-fns/format';
import common from 'constants/common';
import EditRowMenu from './editRowMenu';
import EditIcon from './editIcon';
import EditableRow from './editableRow';
import DateInput from '../dateInput';

const EditableTableRow = ({
  medication,
  author,
  rowIndex,
  editingRowNumber,
  changeRowNumber,
  editing,
  isRowEditing,
  onEdit,
  onDelete,
  messageErrors,
  canEditPage,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isEditing, setEditing] = useState(false);
  const [editableMedication, setEditableMedication] = useState({
    ...medication,
  });
  const inputRef = useRef();
  const ref = useRef();
  useOutsideClick(ref, () => {
    setShowMenu(false);
  });

  const setRow = (data) => {
    setEditableMedication(data);
    onEdit(data);
  };

  const editSelectedRow = (row) => {
    onEdit(editableMedication);
    editing(true);
    setEditing(true);
    changeRowNumber(row);
  };

  useEffect(() => {
    if (!isRowEditing) {
      setEditing(false);
      setEditableMedication(medication);
    }
  }, [isRowEditing]);

  return (
    <tr
      className={classNames({
        'editable-row': true,
        'editable-row--edit':
          showMenu ||
          (isEditing && isRowEditing && rowIndex === editingRowNumber),
      })}
      ref={ref}
    >
      <td
        className={classNames('editable-row__td-menu', {
          'editable-row__td-menu__disabled': !canEditPage,
        })}
        onClick={() => setShowMenu(!showMenu)}
      >
        {editableMedication.name}
        {showMenu ||
          (isEditing && (
            <div className="editable-row__edit-indicator">
              <EditIcon />
            </div>
          ))}
        {canEditPage && showMenu && (
          <EditRowMenu
            lastEdited={author}
            onEdit={() => editSelectedRow(rowIndex)}
            onDelete={onDelete}
          />
        )}
      </td>
      <td
        className={classNames({
          'table-column__input': true,
          'editing-row':
            isEditing && isRowEditing && rowIndex === editingRowNumber,
        })}
      >
        <div
          className={classNames('activity-status', {
            'activity-status-normal': editableMedication.isActive,
            'activity-status-inactive': !editableMedication.isActive,
          })}
        >
          {editableMedication.isActive ? 'Active' : 'Inactive'}
        </div>
      </td>
      <td
        className={classNames('ccm-datepicker-mobile-1', {
          'table-column__input': true,
          'editing-row': isEditing,
        })}
      >
        <DateInput
          date={editableMedication.startDate}
          readOnly={!isEditing}
          onSelect={(date) => {
            setRow({
              ...editableMedication,
              startDate: format(
                date,
                common.dateFormats.priorAuthorizationAdmin,
              ),
            });
          }}
          onClear={() => {
            setRow({
              ...editableMedication,
              startDate: null,
            });
          }}
        />
        {isEditing && messageErrors.startDate ? (
          <div className="table-column__error">{messageErrors.startDate}</div>
        ) : null}
      </td>
      <td
        className={classNames('ccm-datepicker-mobile-2', {
          'table-column__input': true,
          'editing-row': isEditing,
        })}
      >
        <DateInput
          date={editableMedication.endDate}
          readOnly={!isEditing}
          onSelect={(date) => {
            setRow({
              ...editableMedication,
              endDate: format(date, common.dateFormats.priorAuthorizationAdmin),
            });
          }}
          onClear={() => {
            setRow({
              ...editableMedication,
              endDate: null,
            });
          }}
        />
        {isEditing && messageErrors.endDate ? (
          <div className="table-column__error">{messageErrors.endDate}</div>
        ) : null}
      </td>
      <td
        className={classNames({
          'table-column__input': true,
          'editing-row':
            isEditing && isRowEditing && rowIndex === editingRowNumber,
        })}
      >
        {isEditing && isRowEditing && rowIndex === editingRowNumber ? (
          <EditableRow
            text={editableMedication.duration}
            placeholder={
              editableMedication.duration
                ? editableMedication.duration
                : 'Duration'
            }
            childRef={inputRef}
            type="input"
          >
            <div className="table-column__input-container">
              <Input
                ref={inputRef}
                plaintext
                bsSize="sm"
                defaultValue={editableMedication.duration}
                onChange={(e) =>
                  setRow({
                    ...editableMedication,
                    duration: e.target.value,
                  })
                }
              />
            </div>
          </EditableRow>
        ) : (
          editableMedication.duration
        )}
      </td>
      <td
        className={classNames('medication-table-dosage', {
          'table-column__input': true,
          'editing-row':
            isEditing && isRowEditing && rowIndex === editingRowNumber,
        })}
      >
        {isEditing && isRowEditing && rowIndex === editingRowNumber ? (
          <EditableRow
            text={editableMedication.dosage}
            placeholder={
              editableMedication.dosage ? editableMedication.dosage : 'Dosage'
            }
            childRef={inputRef}
            type="input"
          >
            <div className="table-column__input-container">
              <Input
                ref={inputRef}
                plaintext
                bsSize="sm"
                defaultValue={editableMedication.dosage}
                onChange={(e) =>
                  setRow({
                    ...editableMedication,
                    dosage: e.target.value,
                  })
                }
              />
            </div>
          </EditableRow>
        ) : (
          editableMedication.dosage
        )}
      </td>
      <td
        className={classNames({
          'table-column__input': true,
          'editing-row':
            isEditing && isRowEditing && rowIndex === editingRowNumber,
        })}
      >
        {isEditing && isRowEditing && rowIndex === editingRowNumber ? (
          <EditableRow
            text={editableMedication.route}
            placeholder={
              editableMedication.route ? editableMedication.route : 'Route'
            }
            childRef={inputRef}
            type="input"
          >
            <div className="table-column__input-container">
              <Input
                ref={inputRef}
                plaintext
                bsSize="sm"
                defaultValue={editableMedication.route}
                onChange={(e) =>
                  setRow({
                    ...editableMedication,
                    route: e.target.value,
                  })
                }
              />
            </div>
          </EditableRow>
        ) : (
          editableMedication.route
        )}
      </td>
      <td
        className={classNames({
          'table-column__input': true,
          'editing-row':
            isEditing && isRowEditing && rowIndex === editingRowNumber,
        })}
      >
        {isEditing && isRowEditing && rowIndex === editingRowNumber ? (
          <EditableRow
            text={editableMedication.frequency}
            placeholder={
              editableMedication.frequency
                ? editableMedication.frequency
                : 'Frequency'
            }
            childRef={inputRef}
            type="input"
          >
            <div className="table-column__input-container">
              <Input
                ref={inputRef}
                plaintext
                bsSize="sm"
                defaultValue={editableMedication.frequency}
                onChange={(e) =>
                  setRow({
                    ...editableMedication,
                    frequency: e.target.value,
                  })
                }
              />
            </div>
          </EditableRow>
        ) : (
          editableMedication.frequency
        )}
      </td>
      <td
        className={classNames({
          'table-column__input': true,
          'editing-row':
            isEditing && isRowEditing && rowIndex === editingRowNumber,
        })}
      >
        {isEditing && isRowEditing && rowIndex === editingRowNumber ? (
          <EditableRow
            text={editableMedication.note}
            placeholder={
              editableMedication.note ? editableMedication.note : 'Note'
            }
            childRef={inputRef}
            type="input"
          >
            <div className="table-column__input-container">
              <Input
                ref={inputRef}
                plaintext
                bsSize="sm"
                defaultValue={editableMedication.note}
                onChange={(e) =>
                  setRow({
                    ...editableMedication,
                    note: e.target.value,
                  })
                }
              />
            </div>
          </EditableRow>
        ) : (
          editableMedication.note
        )}
      </td>
    </tr>
  );
};

EditableTableRow.propTypes = {
  medication: PropTypes.shape().isRequired,
  author: PropTypes.shape().isRequired,
  rowIndex: PropTypes.number.isRequired,
  editingRowNumber: PropTypes.number.isRequired,
  changeRowNumber: PropTypes.func.isRequired,
  editing: PropTypes.func,
  isRowEditing: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  messageErrors: PropTypes.shape(),
  canEditPage: PropTypes.bool,
};

export default EditableTableRow;
